import React, { useMemo } from 'react';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Numeric } from '@swyftx/aviary/atoms/Typography';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { observer } from 'mobx-react-lite';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginInstrumentDetails } from 'src/lib/margin/hooks/instruments';
import { useMarginPositionDetails } from 'src/lib/margin/hooks/positions';

import { MarginPositionRowType } from '../MarginPositionsTable.data';

type Props = {
  positionId?: string;
  positionType: MarginPositionRowType;
};

const MarginPositionsTableLotsProfitLoss: React.FC<Props> = observer(({ positionId, positionType }) => {
  const { position, side, price, isOpen, priceScale, positionMarket } = useMarginPositionDetails({ positionId });
  const { symbol } = useMarginInstrumentDetails({ instrument: positionMarket });
  const { currency } = useMarginAccount();

  const title = useMemo(
    () =>
      position ? (
        <Numeric weight='bold' color='primary'>
          {position.volume} {symbol}
        </Numeric>
      ) : null,
    [position, symbol],
  );

  const subTitle = useMemo(() => {
    if (!position) return null;

    switch (positionType) {
      case 'stoploss':
        return position.stopLoss ? (
          <Numeric color='secondary'>@ {formatCurrency(position.stopLoss, undefined, { priceScale })}</Numeric>
        ) : (
          <Numeric color='secondary'>-</Numeric>
        );
      case 'takeprofit':
        return position.takeProfit ? (
          <Numeric color='secondary'>@ {formatCurrency(position.takeProfit, undefined, { priceScale })}</Numeric>
        ) : (
          <Numeric color='secondary'>-</Numeric>
        );
      case 'position':
        if (side === undefined) return null;

        if (!isOpen) return <Numeric color='secondary'>@ {formatCurrency(price, undefined, { priceScale })}</Numeric>;

        return (
          <FlexLayout spacing={4} justifyContent='end'>
            {position.profit ? (
              <>
                <Numeric color={Big(position.profit).gte(0) ? 'success' : 'error'} weight='bold'>
                  {formatCurrency(Big(position.profit), currency)}
                </Numeric>
                <Numeric color={Big(position.profit).gte(0) ? 'success' : 'error'}>{currency?.code}</Numeric>
              </>
            ) : (
              <Numeric color='secondary'>-</Numeric>
            )}
          </FlexLayout>
        );
      default:
        positionType satisfies never;
    }
  }, [currency, isOpen, position, positionType, price, priceScale, side]);

  if (!position) return null;

  return (
    <FlexLayout justifyContent='start' direction='column' alignItems='end'>
      {title}
      {subTitle}
    </FlexLayout>
  );
});

export { MarginPositionsTableLotsProfitLoss };
